import { AlertCircle, CheckCircle, Clock, Edit, Eye, X, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import { Input, InputGroup } from '../components/common/Input';
import PageLayout from '../components/layout/PageLayout';
import ordersService from '../services/ordersService';
import { theme } from '../styles/theme';

const ActionsBar = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};
  padding: ${theme.spacing.lg};
  background: linear-gradient(135deg, ${theme.colors.white} 0%, ${theme.colors.secondary} 100%);
  border-radius: ${theme.borderRadius.xl};
  box-shadow: ${theme.shadows.md};
  flex-wrap: wrap;
  border: 1px solid ${theme.colors.border};
  transition: all ${theme.transitions.duration.base};

  &:hover {
    box-shadow: ${theme.shadows.lg};
  }
`;

const FilterButtons = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  flex-wrap: wrap;
`;

const FilterButton = styled.button`
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  border-radius: ${theme.borderRadius.full};
  border: 2px solid ${({ active }) => active ? theme.colors.primary : theme.colors.border};
  background: ${({ active }) => active 
    ? `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primaryLight} 100%)` 
    : theme.colors.white};
  color: ${({ active }) => active ? theme.colors.white : theme.colors.text};
  font-weight: ${({ active }) => active ? theme.typography.fontWeight.semiBold : theme.typography.fontWeight.regular};
  font-family: ${theme.typography.fontFamily};
  cursor: pointer;
  transition: all ${theme.transitions.duration.base} ${theme.transitions.easing.easeOut};
  font-size: ${theme.typography.fontSize.sm};
  box-shadow: ${({ active }) => active ? theme.shadows.sm : 'none'};

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.md};
    border-color: ${theme.colors.primary};
    ${({ active }) => !active && `
      background: ${theme.colors.secondary};
    `}
  }

  &:active {
    transform: translateY(0);
  }
`;

const OrdersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: ${theme.spacing.lg};

  @media (max-width: ${theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.md};
  }
`;

const OrderCard = styled(Card)`
  padding: ${theme.spacing.lg};
  border-left: 4px solid ${({ status }) => {
    if (status === 'completed') return theme.colors.status.ready;
    if (status === 'preparing') return theme.colors.status.preparing;
    if (status === 'cancelled') return theme.colors.status.outOfStock;
    return theme.colors.status.pending;
  }};
`;

const OrderHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${theme.spacing.md};
  padding-bottom: ${theme.spacing.md};
  border-bottom: 2px solid ${theme.colors.border};

  div {
    h3 {
      font-size: ${theme.typography.fontSize.lg};
      font-weight: ${theme.typography.fontWeight.bold};
      margin-bottom: ${theme.spacing.xs};
      color: ${theme.colors.text};
    }

    p {
      font-size: ${theme.typography.fontSize.sm};
      color: ${theme.colors.textSecondary};
    }
  }
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.semiBold};
  background-color: ${({ status }) => {
    if (status === 'completed') return `${theme.colors.status.ready}20`;
    if (status === 'preparing') return `${theme.colors.status.preparing}20`;
    if (status === 'cancelled') return `${theme.colors.status.outOfStock}20`;
    return `${theme.colors.status.pending}20`;
  }};
  color: ${({ status }) => {
    if (status === 'completed') return theme.colors.status.ready;
    if (status === 'preparing') return theme.colors.status.preparing;
    if (status === 'cancelled') return theme.colors.status.outOfStock;
    return theme.colors.status.pending;
  }};
`;

const OrderDetails = styled.div`
  margin-bottom: ${theme.spacing.md};
`;

const OrderItem = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${theme.spacing.sm};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text};
`;

const OrderTotal = styled.div`
  display: flex;
  justify-content: space-between;
  padding-top: ${theme.spacing.md};
  border-top: 2px solid ${theme.colors.border};
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.primary};
  margin-bottom: ${theme.spacing.md};
`;

const OrderActions = styled.div`
  display: flex;
  gap: ${theme.spacing.sm};
  flex-wrap: wrap;
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${theme.zIndex.modal};
  padding: ${theme.spacing.lg};
`;

const ModalContent = styled(Card)`
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.lg};
  padding-bottom: ${theme.spacing.md};
  border-bottom: 2px solid ${theme.colors.border};

  h2 {
    font-size: ${theme.typography.fontSize.xl};
    font-weight: ${theme.typography.fontWeight.semiBold};
    color: ${theme.colors.text};
  }
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: ${theme.borderRadius.md};
  border: none;
  background-color: ${theme.colors.secondary};
  color: ${theme.colors.text};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${theme.colors.status.outOfStock};
    color: ${theme.colors.white};
  }
`;

// Orders fetch logic lives inside the `OrdersManagement` component (see below)

const statusFilters = ['All', 'Pending', 'Preparing', 'Completed', 'Cancelled'];

function OrdersManagement() {
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [ordersError, setOrdersError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showModifyModal, setShowModifyModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [orderToCancel, setOrderToCancel] = useState(null);
  // Date filter state
  const [dateFilter, setDateFilter] = useState('today');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    fetchOrders();
  }, []);

  async function fetchOrders() {
    setLoadingOrders(true);
    setOrdersError(null);
    try {
      const data = await ordersService.getAll();
      setOrders(Array.isArray(data) ? data : []);
    } catch (err) {
      setOrdersError(err.message || 'Failed to load orders');
    } finally {
      setLoadingOrders(false);
    }
  }

  const handleStatusUpdate = async (orderId, newStatus) => {
    try {
      await ordersService.update(orderId, { status: newStatus });
      setOrders((prev) =>
        prev.map((order) =>
          order.id === orderId ? { ...order, status: newStatus } : order
        )
      );
    } catch (err) {
      alert(err.message || 'Failed to update status');
    }
  };

  const handleCancelOrder = (order) => {
    setOrderToCancel(order);
    setCancelReason('');
    setShowCancelModal(true);
  };

  const handleConfirmCancel = async () => {
    if (!cancelReason.trim()) {
      alert('Please provide a cancellation reason');
      return;
    }
    try {
      await ordersService.update(orderToCancel.id, { status: 'cancelled', cancelReason });
      setOrders((prev) =>
        prev.map((order) =>
          order.id === orderToCancel.id
            ? { ...order, status: 'cancelled', cancelReason }
            : order
        )
      );
      setShowCancelModal(false);
      setCancelReason('');
      setOrderToCancel(null);
    } catch (err) {
      alert(err.message || 'Failed to cancel order');
    }
  };

  const handleModifyOrder = (order) => {
    setSelectedOrder(order);
    setShowModifyModal(true);
  };

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setShowModal(true);
  };

  // Date helpers
  const getTodayDate = () => new Date().toISOString().split('T')[0];
  const getYesterdayDate = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split('T')[0];
  };

  const filteredOrders = orders.filter((order) => {
    const matchesSearch = 
      order.orderNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.tableNumber.toString().includes(searchTerm) ||
      order.waiterName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'All' || 
      order.status === statusFilter.toLowerCase();
    // Date filter
    let matchesDate = true;
    if (dateFilter === 'today') {
      matchesDate = order.createdAt && order.createdAt.split('T')[0] === getTodayDate();
    } else if (dateFilter === 'yesterday') {
      matchesDate = order.createdAt && order.createdAt.split('T')[0] === getYesterdayDate();
    } else if (dateFilter === 'custom') {
      if (startDate && endDate) {
        matchesDate = order.createdAt && order.createdAt.split('T')[0] >= startDate && order.createdAt.split('T')[0] <= endDate;
      } else if (startDate) {
        matchesDate = order.createdAt && order.createdAt.split('T')[0] >= startDate;
      } else if (endDate) {
        matchesDate = order.createdAt && order.createdAt.split('T')[0] <= endDate;
      }
    }
    return matchesSearch && matchesStatus && matchesDate;
  });

  return (
    <PageLayout 
      title="Orders Management" 
      description="View, manage, and track all restaurant orders"
    >

      {/* Sorting/Date Filter Bar */}
      <ActionsBar>
        <FilterButtons>
          <FilterButton active={dateFilter === 'today'} onClick={() => setDateFilter('today')}>
            Today
          </FilterButton>
          <FilterButton active={dateFilter === 'yesterday'} onClick={() => setDateFilter('yesterday')}>
            Yesterday
          </FilterButton>
          <FilterButton active={dateFilter === 'custom'} onClick={() => setDateFilter('custom')}>
            Date Range
          </FilterButton>
          {dateFilter === 'custom' && (
            <>
              <Input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                style={{ minWidth: 120 }}
              />
              <span style={{ color: theme.colors.textSecondary }}>to</span>
              <Input
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                style={{ minWidth: 120 }}
              />
            </>
          )}
        </FilterButtons>
      </ActionsBar>

      {/* Search and Status Filter Bar */}
      <ActionsBar>
        <Input
          type="text"
          placeholder="Search orders..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ maxWidth: '300px' }}
        />
        <FilterButtons>
          {statusFilters.map((filter) => (
            <FilterButton
              key={filter}
              active={statusFilter === filter}
              onClick={() => setStatusFilter(filter)}
            >
              {filter}
            </FilterButton>
          ))}
        </FilterButtons>
      </ActionsBar>

        <OrdersGrid>
          {loadingOrders ? (
            <Card style={{ gridColumn: '1 / -1', textAlign: 'center', padding: theme.spacing['3xl'] }}>
              <p style={{ color: theme.colors.textSecondary }}>Loading orders...</p>
            </Card>
          ) : ordersError ? (
            <Card style={{ gridColumn: '1 / -1', textAlign: 'center', padding: theme.spacing['3xl'] }}>
              <p style={{ color: theme.colors.textSecondary }}>{ordersError}</p>
            </Card>
          ) : filteredOrders.length === 0 ? (
            <Card style={{ gridColumn: '1 / -1', textAlign: 'center', padding: theme.spacing['3xl'] }}>
              <p style={{ color: theme.colors.textSecondary }}>No orders found</p>
            </Card>
          ) : (
            filteredOrders.map((order) => (
              <OrderCard key={order.id} status={order.status}>
                <OrderHeader>
                  <div>
                    <h3>{order.orderNumber}</h3>
                    <p>Table {order.tableNumber} • {order.waiterName} • {order.timestamp}</p>
                    <p style={{ fontSize: 12, color: theme.colors.textSecondary, marginTop: 2 }}>
                      Payment: {order.paymentMethod ? order.paymentMethod.charAt(0).toUpperCase() + order.paymentMethod.slice(1) : 'N/A'}
                    </p>
                  </div>
                  <StatusBadge status={order.status}>
                    {order.status === 'completed' && <CheckCircle size={14} />}
                    {order.status === 'preparing' && <Clock size={14} />}
                    {order.status === 'cancelled' && <XCircle size={14} />}
                    {order.status === 'pending' && <Clock size={14} />}
                    {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                  </StatusBadge>
                </OrderHeader>

                <OrderDetails>
                  {order.items.map((item, index) => (
                    <OrderItem key={index}>
                      <span>{item.quantity}x {item.itemName || item.name}</span>
                      <span>₹{item.price * item.quantity}</span>
                    </OrderItem>
                  ))}
                </OrderDetails>

                <OrderTotal>
                  <span>Total:</span>
                  <span>₹{order.total}</span>
                </OrderTotal>

                <OrderActions>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleViewDetails(order)}
                  >
                    <Eye size={16} />
                    View Details
                  </Button>
                  {order.status === 'pending' && (
                    <>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => handleStatusUpdate(order.id, 'preparing')}
                      >
                        Start Preparing
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleModifyOrder(order)}
                      >
                        <Edit size={16} />
                        Modify
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleCancelOrder(order)}
                      >
                        <X size={16} />
                        Cancel
                      </Button>
                    </>
                  )}
                  {order.status === 'preparing' && (
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => handleStatusUpdate(order.id, 'completed')}
                    >
                      Mark Complete
                    </Button>
                  )}
                </OrderActions>
              </OrderCard>
            ))
          )}
        </OrdersGrid>

        {showModal && selectedOrder && (
          <Modal onClick={() => setShowModal(false)}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalHeader>
                <h2>Order Details - {selectedOrder.orderNumber}</h2>
                <CloseButton onClick={() => setShowModal(false)}>×</CloseButton>
              </ModalHeader>
              <div>
                <p><strong>Table:</strong> {selectedOrder.tableNumber}</p>
                <p><strong>Waiter:</strong> {selectedOrder.waiterName}</p>
                <p><strong>Time:</strong> {selectedOrder.timestamp}</p>
                <p><strong>Status:</strong> {selectedOrder.status}</p>
                {selectedOrder.customerNotes && (
                  <p><strong>Notes:</strong> {selectedOrder.customerNotes}</p>
                )}
                <div style={{ marginTop: theme.spacing.lg }}>
                  <h3 style={{ marginBottom: theme.spacing.md }}>Items:</h3>
                  {selectedOrder.items.map((item, index) => (
                    <OrderItem key={index}>
                      <span>{item.quantity}x {item.itemName || item.name}</span>
                      <span>₹{item.price * item.quantity}</span>
                    </OrderItem>
                  ))}
                  <OrderTotal>
                    <span>Total:</span>
                    <span>₹{selectedOrder.total}</span>
                  </OrderTotal>
                </div>
              </div>
            </ModalContent>
          </Modal>
        )}

        {showCancelModal && orderToCancel && (
          <Modal onClick={() => setShowCancelModal(false)}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalHeader>
                <h2>Cancel Order - {orderToCancel.orderNumber}</h2>
                <CloseButton onClick={() => setShowCancelModal(false)}>×</CloseButton>
              </ModalHeader>
              <div>
                <p style={{ marginBottom: theme.spacing.md }}>
                  <AlertCircle size={20} style={{ color: theme.colors.status.outOfStock, marginRight: theme.spacing.sm }} />
                  Please provide a reason for cancellation
                </p>
                <InputGroup>
                  <label>Cancellation Reason *</label>
                  <select
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '0.75rem 1rem',
                      fontSize: theme.typography.fontSize.base,
                      fontFamily: theme.typography.fontFamily,
                      border: `2px solid ${theme.colors.border}`,
                      borderRadius: theme.borderRadius.md,
                      backgroundColor: theme.colors.white,
                    }}
                    required
                  >
                    <option value="">Select reason...</option>
                    <option value="Customer Request">Customer Request</option>
                    <option value="Item Unavailable">Item Unavailable</option>
                    <option value="Kitchen Issue">Kitchen Issue</option>
                    <option value="Payment Issue">Payment Issue</option>
                    <option value="Duplicate Order">Duplicate Order</option>
                    <option value="Other">Other</option>
                  </select>
                </InputGroup>
                {cancelReason === 'Other' && (
                  <InputGroup>
                    <label>Please specify</label>
                    <Input
                      type="text"
                      placeholder="Enter reason..."
                      value={cancelReason}
                      onChange={(e) => setCancelReason(e.target.value)}
                      required
                    />
                  </InputGroup>
                )}
                <div style={{ display: 'flex', gap: theme.spacing.md, justifyContent: 'flex-end', marginTop: theme.spacing.lg }}>
                  <Button variant="secondary" onClick={() => setShowCancelModal(false)}>
                    Cancel
                  </Button>
                  <Button variant="primary" onClick={handleConfirmCancel}>
                    Confirm Cancellation
                  </Button>
                </div>
              </div>
            </ModalContent>
          </Modal>
        )}

        {showModifyModal && selectedOrder && (
          <Modal onClick={() => setShowModifyModal(false)}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalHeader>
                <h2>Modify Order - {selectedOrder.orderNumber}</h2>
                <CloseButton onClick={() => setShowModifyModal(false)}>×</CloseButton>
              </ModalHeader>
              <div>
                <p style={{ marginBottom: theme.spacing.lg }}>Modify items, quantities, or add/remove items from this order.</p>
                <div style={{ marginBottom: theme.spacing.lg }}>
                  <h3 style={{ marginBottom: theme.spacing.md }}>Current Items:</h3>
                  {selectedOrder.items.map((item, index) => (
                    <div key={index} style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      padding: theme.spacing.md,
                      background: theme.colors.secondary,
                      borderRadius: theme.borderRadius.md,
                      marginBottom: theme.spacing.sm
                    }}>
                      <span>{item.quantity}x {item.name}</span>
                      <div style={{ display: 'flex', gap: theme.spacing.sm, alignItems: 'center' }}>
                        <Button size="sm" variant="outline">-</Button>
                        <span>{item.quantity}</span>
                        <Button size="sm" variant="outline">+</Button>
                        <Button size="sm" variant="secondary" style={{ marginLeft: theme.spacing.sm }}>
                          <X size={14} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: theme.spacing.md, justifyContent: 'flex-end', marginTop: theme.spacing.lg }}>
                  <Button variant="secondary" onClick={() => setShowModifyModal(false)}>
                    Cancel
                  </Button>
                  <Button variant="primary" onClick={() => {
                    alert('Order modified successfully!');
                    setShowModifyModal(false);
                  }}>
                    Save Changes
                  </Button>
                </div>
              </div>
            </ModalContent>
          </Modal>
        )}
    </PageLayout>
  );
}

export default OrdersManagement;

